import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityAdapter,
  EntityId,
  EntityState,
  Slice,
} from '@reduxjs/toolkit'
import api from '@services/api.service'
import { formatDate } from '../../tools/time.tools'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

export interface AppState<T> extends EntityState<T, EntityId> {
  loading: boolean
  inProgress: EntityId[]
  error: string | null
}

type FilterParamValue = string | number | boolean

export enum SortOrder {
  ASCENDING = '',
  DESCENDING = '-',
}

export interface DjangoModel {
  id: EntityId
  updatedBy: string
  updatedAt: string
  createdBy: string
  createdAt: string

  [key: string]: any
}

export interface FilterableModel extends DjangoModel {
  [key: string]: FilterParamValue
}

export interface DjangoErrorResponse {
  message: string
}

export class DjangoFilter<T extends FilterableModel> {
  private params: Partial<DjangoModel>
  private sortOrder: SortOrder
  private sortFields: (keyof T)[] = []
  private search?: string
  private limit?: number
  private offset?: number

  constructor(initialParams: Partial<T> = {}) {
    this.params = initialParams
    this.sortOrder = SortOrder.ASCENDING
  }

  addFilter(newFilter: Partial<T>): void {
    this.params = { ...this.params, ...newFilter }
  }

  setSortOrder(sortOrder: SortOrder): void {
    this.sortOrder = sortOrder
  }

  addSortField(sortField: keyof T): void {
    this.sortFields.push(sortField)
  }

  setSearch(search: string): void {
    this.search = search
  }

  setLimit(limit: number): void {
    this.limit = limit
  }

  setOffset(offset: number): void {
    this.offset = offset
  }

  addDateFilter(
    fieldName: keyof T,
    start: Date | null,
    end: Date | null
  ): void {
    if (start) {
      const startDateString = formatDate(start)
      this.params = {
        ...this.params,
        [`${fieldName.toString()}_after`]: startDateString,
      }
    }

    if (end) {
      const endDateString = formatDate(end)
      this.params = {
        ...this.params,
        [`${fieldName.toString()}_before`]: endDateString,
      }
    }
  }

  getFilter(): string {
    const urlParams = new URLSearchParams()

    for (const [key, value] of Object.entries(this.params)) {
      if (value) urlParams.append(key, value.toString())
    }

    if (this.sortFields.length > 0) {
      urlParams.append(
        'ordering',
        this.sortFields
          .map((field) => `${this.sortOrder}${field.toString()}`)
          .join(',')
      )
    }

    if (this.search) {
      urlParams.append('search', this.search)
    }
    if (this.limit) {
      urlParams.append('limit', this.limit.toString())
    }

    if (this.offset) {
      urlParams.append('offset', this.offset.toString())
    }

    return urlParams.toString()
  }
}

// Generic API service functions
const createGenericThunks = <T extends DjangoModel>(
  entityName: string,
  endpoint: string
) => {
  return {
    fetchAll: createAsyncThunk(
      `${entityName}/fetchAll`,
      async (djangoFilter?: DjangoFilter<T>) => {
        try {
          const filterString = djangoFilter?.getFilter()
          const response = await api.get(
            `${endpoint}/?${filterString ? filterString : ''}`
          )
          if (Array.isArray(response.data)) {
            return response.data
          }
          return response.data.results
        } catch (error) {
          const axiosError: AxiosError = error as AxiosError
          const djangoError: DjangoErrorResponse = axiosError.response
            ?.data as DjangoErrorResponse
          toast.error(`Error: ${djangoError.message || 'Could not fetch data'}`)
          throw djangoError
        }
      }
    ),
    fetchById: createAsyncThunk(
      `${entityName}/fetchById`,
      async (id: EntityId) => {
        try {
          const response = await api.get(`${endpoint}/${id}/`)
          return response.data
        } catch (error) {
          const axiosError: AxiosError = error as AxiosError
          const djangoError: DjangoErrorResponse = axiosError.response
            ?.data as DjangoErrorResponse
          toast.error(
            `Error: ${djangoError.message || 'Could not fetch entity by id'}`
          )
          throw djangoError
        }
      }
    ),
    create: createAsyncThunk(`${entityName}/`, async (data: Partial<T>) => {
      try {
        const response = await api.post(`${endpoint}/`, data)
        return response.data
      } catch (error) {
        const axiosError: AxiosError = error as AxiosError
        const djangoError: DjangoErrorResponse = axiosError.response
          ?.data as DjangoErrorResponse
        toast.error(
          `Error: ${djangoError.message || 'Could not create entity'}`
        )
        throw djangoError
      }
    }),
    update: createAsyncThunk(
      `${entityName}/update`,
      async ({ id, data }: { id: EntityId; data: Partial<T> }) => {
        try {
          const response = await api.put(`${endpoint}/${id}/`, data)
          return response.data
        } catch (error) {
          const axiosError: AxiosError = error as AxiosError
          const djangoError: DjangoErrorResponse = axiosError.response
            ?.data as DjangoErrorResponse
          toast.error(
            `Error: ${djangoError.message || 'Could not update entity'}`
          )
          throw djangoError
        }
      }
    ),
    delete: createAsyncThunk(`${entityName}/delete`, async (id: EntityId) => {
      try {
        await api.delete(`${endpoint}/${id}/`)
        return id // Returning the ID of the deleted entity
      } catch (error) {
        const axiosError: AxiosError = error as AxiosError
        const djangoError: DjangoErrorResponse = axiosError.response
          ?.data as DjangoErrorResponse
        toast.error(
          `Error: ${djangoError.message || 'Could not delete entity'}`
        )
        throw djangoError
      }
    }),
  }
}

// Create a generic entity adapter and export it
const createGenericAdapter = <T extends DjangoModel>() =>
  createEntityAdapter<T>()

// Generic Slice Factory
export const createGenericSlice = <T extends DjangoModel>(
  entityName: string,
  entityEndpoint: string,
  initialState: Partial<AppState<T>>,
  extraReducers?: (builder: any) => void
) => {
  const adapter: EntityAdapter<T, T['id']> = createGenericAdapter<T>()
  const thunks = createGenericThunks<T>(entityName, entityEndpoint)

  const slice: Slice<AppState<T>> = createSlice({
    name: entityName,
    initialState: adapter.getInitialState({
      loading: false,
      inProgress: [],
      error: null,
      ...initialState,
    }),
    reducers: {
      // @ts-ignore
      addOne: adapter.addOne, // @ts-ignore
      addMany: adapter.addMany, // @ts-ignore
      removeAll: adapter.removeAll, // @ts-ignore
      removeOne: adapter.removeOne, // @ts-ignore
      removeMany: adapter.removeMany, // @ts-ignore
      updateOne: adapter.updateOne, // @ts-ignore
      updateMany: adapter.updateMany, // @ts-ignore
      upsertOne: adapter.upsertOne, // @ts-ignore
      upsertMany: adapter.upsertMany, // @ts-ignore
      setAll: adapter.setAll, // @ts-ignore
      setOne: adapter.setOne, // @ts-ignore
      setMany: adapter.setMany,
    },
    extraReducers: (builder) => {
      // Fetch all entities
      builder
        .addCase(thunks.fetchAll.pending, (state) => {
          state.loading = true
          state.error = null
        })
        .addCase(thunks.fetchAll.fulfilled, (state, action) => {
          adapter.upsertMany(state, action.payload)
          state.loading = false
        })
        .addCase(thunks.fetchAll.rejected, (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Failed to fetch data'
        })

      // Fetch single entity by ID
      builder
        .addCase(thunks.fetchById.pending, (state, action) => {
          state.inProgress = [action.meta.arg, ...state.inProgress]
          state.error = null
        })
        .addCase(thunks.fetchById.fulfilled, (state, action) => {
          adapter.upsertOne(state, action.payload)
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg
          )
        })
        .addCase(thunks.fetchById.rejected, (state, action) => {
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg
          )
          state.error = action.error.message || 'Failed to fetch data'
        })

      // Create new entity
      builder
        .addCase(thunks.create.pending, (state) => {
          state.loading = true
          state.error = null
        })
        .addCase(thunks.create.fulfilled, (state, action) => {
          adapter.addOne(state, action.payload)
          state.loading = false
        })
        .addCase(thunks.create.rejected, (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Failed to create data'
        })

      // Update existing entity
      builder
        .addCase(thunks.update.pending, (state, action) => {
          state.inProgress = [action.meta.arg.id, ...state.inProgress]
          state.error = null
        })
        .addCase(thunks.update.fulfilled, (state, action) => {
          adapter.updateOne(state, {
            id: action.meta.arg.id,
            changes: action.payload,
          })
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg.id
          )
        })
        .addCase(thunks.update.rejected, (state, action) => {
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg.id
          )
          state.error = action.error.message || 'Failed to update data'
        })

      // Delete entity
      builder
        .addCase(thunks.delete.pending, (state, action) => {
          state.inProgress = [action.meta.arg, ...state.inProgress]
          state.error = null
        })
        .addCase(thunks.delete.fulfilled, (state, action) => {
          adapter.removeOne(state, action.meta.arg)
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg
          )
        })
        .addCase(thunks.delete.rejected, (state, action) => {
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg
          )
          state.error = action.error.message || 'Failed to delete data'
        })

      if (extraReducers) extraReducers(builder)
    },
  })

  const selectSlice = (state: any) => state[entityName]
  const selectEntities = (state: any) => state[entityName].entities
  const selectLoading = createSelector([selectSlice], (slice) => slice.loading)

  const selectIsIdInProgress = createSelector(
    [selectSlice, (_, id) => id],
    (state, id) => (!!id ? state.inProgress.includes(id) : false)
  )

  const selectError = createSelector([selectSlice], (slice) => slice.error)

  const selectByIds = createSelector(
    [selectEntities, (_, ids) => ids],
    (entities, ids) => ids.map((id: EntityId) => entities[id]).filter(Boolean)
  )

  // Create an object to store all the selectors
  const selectors = {
    selectLoading,
    selectIsIdInProgress,
    selectError,
    selectByIds,
  }

  return { slice, thunks, adapter, selectors }
}
